<script setup lang="ts">
import { useDebounce } from '@vueuse/core'
import {
  ChevronsLeft,
  ChevronLeft,
  ChevronsRight,
  ChevronRight,
  LucideCircleX,
  LucideNewspaper,
  LucideSearch
} from 'lucide-vue-next'
import type { SearchResponse } from 'meilisearch'
import { Alert, AlertDescription } from '~/components/ui/alert'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import {
  Pagination,
  PaginationEllipsis,
  PaginationFirst,
  PaginationLast,
  PaginationList,
  PaginationListItem,
  PaginationNext,
  PaginationPrev
} from '~/components/ui/pagination'
import {
  EntityRatingTargetItem,
  EntityRatingTargetItemSkeleton,
  type IRatingTarget
} from '~/components/entity/rating-target'

const PER_PAGE = 8
// Banks show the whole list at once instead of paging it — small enough to fetch in one call.
const UNPAGINATED_HITS = 500

interface IMeiliRatingHit {
  id: number
  title: Record<string, string>
  logo?: string | null
  url?: string
  organization?: {
    title: Record<string, string>
    url: string
  }
}

const props = withDefaults(
  defineProps<{
    modelValue: number | null
    meiliIndex: string
    attributesToSearchOn: string[]
    emptyText: string
    searchPlaceholder: string
    filter?: string
    /** Banks show the whole list at once instead of paging it. */
    paginated?: boolean
  }>(),
  {
    paginated: true,
    filter: undefined
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: number | null]
  /** Full item behind modelValue, so the parent can render it without a second lookup. */
  'update:selectedItem': [value: IRatingTarget | null]
  /** True once a fetch completes and the index has zero hits for an empty query. */
  'update:empty': [value: boolean]
}>()

const config = useRuntimeConfig()
const { locale } = useI18n()

const query = ref('')
const page = ref(1)
const debouncedQuery = useDebounce(query, 500)

const localized = (value?: Record<string, string> | null) =>
  value?.[locale.value] || value?.ru || ''

const toRatingTarget = (hit: IMeiliRatingHit): IRatingTarget => ({
  id: hit.id,
  title: localized(hit.title),
  logo: hit.logo,
  url: hit.url,
  organization: hit.organization
    ? { title: localized(hit.organization.title), url: hit.organization.url }
    : undefined
})

// Shared by the paged listing below and the one-off id-resolve lookup, so
// both go through the same client setup, projected fields, and error shape.
const searchIndex = async (
  query: string,
  params: { hitsPerPage: number; page?: number; filter?: string }
): Promise<SearchResponse<IMeiliRatingHit> | null> => {
  const { search } = useMeiliIndexSearch(`${config.public.meiliEnvironment}_${props.meiliIndex}`)

  try {
    return (await search(query, {
      sort: ['weight:desc'],
      attributesToSearchOn: props.attributesToSearchOn,
      attributesToRetrieve: ['id', 'title', 'logo', 'url', 'organization.title', 'organization.url'],
      ...params
    })) as unknown as SearchResponse<IMeiliRatingHit>
  } catch (error) {
    console.error(`MeiliSearch error for index "${props.meiliIndex}":`, error)
    return null
  }
}

const searchKey = computed(() =>
  ['reviews-picker', props.meiliIndex, props.filter, debouncedQuery.value, page.value].join('-')
)

const { data: result, pending: isLoading } = await useAsyncData<SearchResponse<IMeiliRatingHit> | null>(
  searchKey,
  () =>
    searchIndex(props.paginated ? debouncedQuery.value : '', {
      hitsPerPage: props.paginated ? PER_PAGE : UNPAGINATED_HITS,
      page: props.paginated ? page.value : 1,
      ...(props.filter ? { filter: props.filter } : {})
    }),
  // useAsyncData already refetches on every `key` change (it watches the key
  // internally) — searchKey folds in query/index/filter/page, so no manual
  // `watch(...) { refresh() }` is needed and would only double-fetch.
  { server: false, default: () => null }
)

// Query-string prefill (?org=5) sets modelValue before the user ever opens
// this picker, so the selected card can't come from a click — resolve it
// once by id instead. Paged/filtered listing above stays untouched by this.
if (props.modelValue) {
  searchIndex('', {
    hitsPerPage: 1,
    filter: props.filter ? `${props.filter} AND id = ${props.modelValue}` : `id = ${props.modelValue}`
  }).then((response) => {
    const hit = response?.hits[0]
    if (hit) emit('update:selectedItem', toRatingTarget(hit))
  })
}

// The unpaginated list (banks) still gets filtered client-side against the
// single fetched page, since there is no server-side query for it.
const unpaginatedItems = computed(() => {
  if (props.paginated) return []

  const hits = result.value?.hits ?? []
  const search = query.value.trim().toLowerCase()
  if (!search) return hits

  return hits.filter((hit) => localized(hit.title).toLowerCase().includes(search))
})

const items = computed<IRatingTarget[]>(() =>
  (props.paginated ? (result.value?.hits ?? []) : unpaginatedItems.value).map(toRatingTarget)
)

const totalPages = computed(() =>
  props.paginated ? Math.max(1, result.value?.totalPages || 1) : 1
)

// Only resets paging back to 1. The actual refetch is not triggered here —
// useAsyncData already watches searchKey (which folds in page, so this
// assignment re-triggers it on its own) and calling refresh() here too would
// fire a second, redundant request for the same change.
watch([debouncedQuery, () => props.meiliIndex, () => props.filter], () => {
  page.value = 1
})

const goToPage = (value: number) => {
  page.value = Math.min(Math.max(1, value), totalPages.value)
}

const selectItem = (item: IRatingTarget) => {
  emit('update:modelValue', item.id)
  emit('update:selectedItem', item)
}

// Distinguishes "this org has no products at all" (unsearched, zero hits)
// from "no results for this search term" — only the former should replace
// the picker with a plain alert.
watch(
  result,
  (value) => {
    if (!value || debouncedQuery.value) return
    emit('update:empty', (value.totalHits ?? value.hits.length) === 0)
  },
  { immediate: true }
)

// The Pagination* primitives wrap their slot in <Button as-child>, which needs
// a single interactive child — FeaturePagination supplies a <NuxtLink>, a
// button-driven pager supplies a <button>.
const arrows = computed(() => [
  { key: 'first', at: 'lead', as: PaginationFirst, icon: ChevronsLeft, page: 1, edge: true },
  { key: 'prev', at: 'lead', as: PaginationPrev, icon: ChevronLeft, page: page.value - 1, edge: false },
  { key: 'next', at: 'trail', as: PaginationNext, icon: ChevronRight, page: page.value + 1, edge: false },
  { key: 'last', at: 'trail', as: PaginationLast, icon: ChevronsRight, page: totalPages.value, edge: true }
])

const arrowsAt = (at: string) => arrows.value.filter((arrow) => arrow.at === at)

const isArrowDisabled = (target: number) =>
  target < 1 || target > totalPages.value || target === page.value

</script>

<template>
  <div class="space-y-3">
    <div class="relative">
      <Input
        v-model="query"
        class="h-[52px] pl-14 pr-14"
        :placeholder="props.searchPlaceholder"
        :aria-label="props.searchPlaceholder"
      />
      <LucideSearch class="absolute left-4 top-3.5 size-6 text-gray-600" />
      <Button
        v-if="query"
        type="button"
        size="icon"
        variant="link"
        class="absolute right-4 top-3.5 size-6 text-red-400 hover:text-red-400"
        :aria-label="$t('feature.reviews_add_form.picker.clear')"
        @click="query = ''"
      >
        <LucideCircleX />
      </Button>
    </div>

    <div v-if="isLoading" class="CardsList">
      <EntityRatingTargetItemSkeleton v-for="i in PER_PAGE" :key="i" />
    </div>

    <Alert v-else-if="!items.length" variant="blue">
      <LucideNewspaper />
      <AlertDescription>
        {{ query ? $t('common.nothing_found') : props.emptyText }}
      </AlertDescription>
    </Alert>

    <template v-else>
      <div class="CardsList">
        <EntityRatingTargetItem
          v-for="item in items"
          :key="item.id"
          :item="item"
          :selected="item.id === props.modelValue"
          :select-label="$t('feature.reviews_add_form.picker.select')"
          :selected-label="$t('feature.reviews_add_form.picker.selected')"
          @select="selectItem(item)"
        />
      </div>

      <!--
        Same reka-ui primitives and wrapper classes as FeaturePagination so the
        control looks identical to every other list on the site. It cannot
        reuse that component directly: FeaturePagination navigates through
        <NuxtLink> page URLs, which inside a form would leave the page and
        collide with the cascade's own query prefill.
      -->
      <div v-if="totalPages > 1" class="mt-7 flex justify-end">
        <Pagination
          v-slot="{ page: currentPage }"
          :items-per-page="PER_PAGE"
          :total="result?.totalHits || 0"
          :sibling-count="$device.isMobile ? 0 : 1"
          :page="page"
          show-edges
          class="flex justify-center"
          aria-label="pagination"
        >
          <PaginationList v-slot="{ items: pageItems }" class="flex items-center gap-1">
            <component
              :is="arrow.as"
              v-for="arrow in arrowsAt('lead')"
              v-show="!arrow.edge || !$device.isMobile"
              :key="arrow.key"
            >
              <button
                type="button"
                :aria-disabled="isArrowDisabled(arrow.page)"
                :class="{ 'pointer-events-none opacity-50': isArrowDisabled(arrow.page) }"
                @click="goToPage(arrow.page)"
              >
                <component :is="arrow.icon" class="size-4" />
              </button>
            </component>

            <template v-for="(item, index) in pageItems">
              <PaginationListItem
                v-if="item.type === 'page'"
                :key="index"
                :value="item.value"
                as-child
              >
                <Button
                  class="h-9 w-9 p-0"
                  :variant="item.value === currentPage ? 'blue' : 'outline-gray'"
                  @click="goToPage(item.value)"
                >
                  {{ item.value }}
                </Button>
              </PaginationListItem>
              <PaginationEllipsis v-else :key="item.type" :index="index" />
            </template>

            <component
              :is="arrow.as"
              v-for="arrow in arrowsAt('trail')"
              v-show="!arrow.edge || !$device.isMobile"
              :key="arrow.key"
            >
              <button
                type="button"
                :aria-disabled="isArrowDisabled(arrow.page)"
                :class="{ 'pointer-events-none opacity-50': isArrowDisabled(arrow.page) }"
                @click="goToPage(arrow.page)"
              >
                <component :is="arrow.icon" class="size-4" />
              </button>
            </component>
          </PaginationList>
        </Pagination>
      </div>
    </template>
  </div>
</template>
